import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronLeftIcon, Check } from "lucide-react";
import { Reactable } from "../../components/ui/reactable";
import { ContainerCard } from "../../components/ui/cards";
import { ReportPageShell } from "../../components/ui/reportPageShell";
import { BRANCH_WISE_ACTIVITY_REPORT } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import {
    BRANCH_FILTER_CHANGED_EVENT,
    getOverviewBranchFilter,
    isOrgAdminUser,
} from "../../utils/branchSession";
import { SegmentSwitch } from "../../components/ui/segmentSwitch";
import { useOrgAdminBranchColumn } from "../../hooks/useOrgAdminBranchColumn";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

const ACTIVITY_OPTIONS = [
    { value: "all", label: "All activities" },
    { value: "payroll", label: "Payroll runs" },
    { value: "penalty", label: "Penalties & fines" },
    { value: "salary_adjustment", label: "Salary adjustments" },
];

const ALL_BRANCHES = "all";

const dropdownTriggerClass = cn(
    "inline-flex items-center justify-between gap-2 min-w-[200px]",
    "h-10 px-3 rounded-lg text-sm font-medium",
    "bg-muted/50 border border-[#0BB5AC]/40 text-foreground",
    "hover:bg-[#0BB5AC]/5 hover:border-[#0BB5AC]/60",
    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#0BB5AC]/30"
);

const dropdownContentClass =
    "min-w-[220px] max-h-72 overflow-y-auto rounded-lg border border-[#0BB5AC]/20 bg-background p-1 shadow-lg";

const dropdownItemClass = (isActive) =>
    cn(
        "rounded-md px-3 py-2 text-sm cursor-pointer",
        isActive
            ? "bg-[#0BB5AC]/10 text-[#0BB5AC] font-medium"
            : "text-foreground hover:bg-[#0BB5AC]/5 hover:text-[#0BB5AC]"
    );

const EmployeeCell = ({ row }) => (
    <div>
        <div className="font-medium text-gray-900 dark:text-white">{row?.employee_name || "-"}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{row?.employee_code || "-"}</div>
    </div>
);

const BranchWiseActivityReport = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [view, setView] = useState("detail");
    const [activityType, setActivityType] = useState("all");
    const [branchFilter, setBranchFilter] = useState(() => {
        const headerBranch = getOverviewBranchFilter();
        return headerBranch ? String(headerBranch) : ALL_BRANCHES;
    });

    useEffect(() => {
        const syncHeaderBranch = () => {
            const headerBranch = getOverviewBranchFilter();
            setBranchFilter(headerBranch ? String(headerBranch) : ALL_BRANCHES);
        };
        window.addEventListener(BRANCH_FILTER_CHANGED_EVENT, syncHeaderBranch);
        return () => window.removeEventListener(BRANCH_FILTER_CHANGED_EVENT, syncHeaderBranch);
    }, []);

    const { branchFilterOptions, branchColumnDef, BranchCell } = useOrgAdminBranchColumn("employee_name");

    const branchDropdownOptions = useMemo(
        () => [{ value: ALL_BRANCHES, label: "All branches" }, ...branchFilterOptions],
        [branchFilterOptions]
    );

    const viewFilter = useMemo(
        () => ({
            view,
            activity_type: activityType,
            ...(branchFilter !== ALL_BRANCHES ? { branch_id: branchFilter } : {}),
        }),
        [view, activityType, branchFilter]
    );

    const branchColumn = useMemo(
        () =>
            branchColumnDef || {
                accessor: "branch",
                Header: "Branch",
                fixed: true,
                filterable: true,
                width: "140px",
                maxWidth: "180px",
                Cell: ({ row }) => <BranchCell record={row?.original || row} />,
            },
        [branchColumnDef, BranchCell]
    );

    const detailColumns = useMemo(
        () => [
            { ...branchColumn, fixed: true },
            { accessor: "employee_name", Header: "Employee", filterable: true, maxWidth: "240px", Cell: EmployeeCell },
            { accessor: "department", Header: "Department", filterable: true, Cell: ({ value }) => value ?? "-" },
            {
                accessor: "activity_type",
                Header: "Activity",
                filterable: true,
                Cell: ({ value }) => (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300">
                        {value || "-"}
                    </span>
                ),
            },
            { accessor: "activity_date", Header: "Date / Time", Cell: ({ value }) => value ?? "-" },
            { accessor: "period", Header: "Period", Cell: ({ value }) => value ?? "-" },
            { accessor: "amount", Header: "Amount (₹)", Cell: ({ value }) => value ?? "-" },
            { accessor: "days", Header: "Days / Deduction", Cell: ({ value }) => value ?? "-" },
            {
                accessor: "status",
                Header: "Status",
                Cell: ({ value }) => (
                    <span
                        className={cn(
                            "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                            value === "Reviewed" || value === "Completed"
                                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                        )}
                    >
                        {value || "-"}
                    </span>
                ),
            },
            { accessor: "details", Header: "Details", Cell: ({ value }) => value ?? "-" },
        ],
        [branchColumn]
    );

    const summaryColumns = useMemo(
        () => [
            { ...branchColumn, fixed: true },
            { accessor: "period", Header: "Period", Cell: ({ value }) => value ?? "-" },
            { accessor: "payroll_runs", Header: "Payroll Runs", Cell: ({ value }) => value ?? 0 },
            { accessor: "late_fine_events", Header: "Late Fine Events", Cell: ({ value }) => value ?? 0 },
            { accessor: "short_duty_events", Header: "Short Duty Events", Cell: ({ value }) => value ?? 0 },
            { accessor: "lop_total", Header: "LOP Total", Cell: ({ value }) => value ?? "-" },
            { accessor: "fine_total", Header: "Fine Total", Cell: ({ value }) => value ?? "-" },
            { accessor: "credit_total", Header: "Credits", Cell: ({ value }) => value ?? "-" },
            { accessor: "debit_total", Header: "Debits", Cell: ({ value }) => value ?? "-" },
            { accessor: "net_pay_total", Header: "Net Pay Total", Cell: ({ value }) => value ?? "-" },
        ],
        [branchColumn]
    );

    if (!isOrgAdminUser()) {
        return <Navigate to="/reports" replace />;
    }

    const selectedActivity = ACTIVITY_OPTIONS.find((opt) => opt.value === activityType) || ACTIVITY_OPTIONS[0];
    const selectedBranch =
        branchDropdownOptions.find((opt) => opt.value === branchFilter) || branchDropdownOptions[0];

    return (
        <ContainerCard className="font-sans">
            <div className="flex flex-wrap justify-between items-center gap-3 mb-4 shrink-0">
                <span className="flex gap-2 items-center">
                    <ChevronLeftIcon onClick={() => navigate("/reports")} className="cursor-pointer" />
                    <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                        Branch Wise Activity Report ({totalRecords})
                    </h1>
                </span>

                <div className="flex flex-wrap items-center gap-2">
                    <DropdownMenu>
                        <DropdownMenuTrigger className={dropdownTriggerClass}>
                            <span className="truncate">{selectedBranch.label}</span>
                            <ChevronDown className="h-4 w-4 shrink-0 text-[#0BB5AC]" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className={dropdownContentClass} sideOffset={6}>
                            {branchDropdownOptions.map((opt) => {
                                const isActive = branchFilter === opt.value;
                                return (
                                    <DropdownMenuItem
                                        key={opt.value}
                                        isActive={isActive}
                                        onClick={() => setBranchFilter(opt.value)}
                                        className={dropdownItemClass(isActive)}
                                    >
                                        <span className="flex-1">{opt.label}</span>
                                        {isActive && <Check className="h-4 w-4 shrink-0 text-[#0BB5AC]" />}
                                    </DropdownMenuItem>
                                );
                            })}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <DropdownMenu>
                        <DropdownMenuTrigger className={dropdownTriggerClass}>
                            <span className="truncate">{selectedActivity.label}</span>
                            <ChevronDown className="h-4 w-4 shrink-0 text-[#0BB5AC]" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className={dropdownContentClass} sideOffset={6}>
                            {ACTIVITY_OPTIONS.map((opt) => {
                                const isActive = activityType === opt.value;
                                return (
                                    <DropdownMenuItem
                                        key={opt.value}
                                        isActive={isActive}
                                        onClick={() => setActivityType(opt.value)}
                                        className={dropdownItemClass(isActive)}
                                    >
                                        <span className="flex-1">{opt.label}</span>
                                        {isActive && <Check className="h-4 w-4 shrink-0 text-[#0BB5AC]" />}
                                    </DropdownMenuItem>
                                );
                            })}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <SegmentSwitch
                        value={view}
                        onChange={setView}
                        options={[
                            { value: "detail", label: "Detail" },
                            { value: "summary", label: "Summary" },
                        ]}
                    />
                </div>
            </div>

            <Reactable
                columns={view === "summary" ? summaryColumns : detailColumns}
                data={data}
                setData={setData}
                setTotalCount={setTotalRecords}
                url={BRANCH_WISE_ACTIVITY_REPORT}
                serverSide={true}
                serverTrigger={`${view}-${activityType}-${branchFilter}`}
                filter={viewFilter}
                printFilter={viewFilter}
                rowSize="sm"
                isCSVRequired={true}
                isPDFRequired={true}
                isDateRequired={true}
            />
        </ContainerCard>
    );
}

export default BranchWiseActivityReport;
